import * as React from 'react'
import { GetStaticPaths, GetStaticProps } from 'next'
import { getProductProps, ProductCorePage, Props } from 'pageCores/product'
import {
	PillowProducts,
	ProtectorProducts,
	FoundationProducts,
	BlanketProducts,
	SheetProducts,
	BundleProducts,
	PodCoverProducts,
	ComforterProducts,
	SprayProducts,
	HypericeProducts,
	NormaTecProducts,
	MattressProducts,
} from 'products/allProducts'
import { config } from 'utils/internationalization'
import { generatePathPermutationsForExperiments } from 'ab_testing'

interface ProductPage {
	[key: string]: { query: { handle: string; defaultVariant: number; showStock: boolean } }
}

const productPages: ProductPage = {
	'the-carbon-air-pillow': {
		query: { handle: PillowProducts.CarbonAirPillow.handle, defaultVariant: 1, showStock: false },
	},
	'waterproof-protector': {
		query: { handle: ProtectorProducts.PodProtector.handle, defaultVariant: 1, showStock: false },
	},
	foundation: { query: { handle: FoundationProducts.SimpleFoundation.handle, defaultVariant: 3, showStock: false } },
	'eight-x-mint-house': { query: { handle: 'eight-x-mint-house', defaultVariant: 1, showStock: false } },
	pod: { query: { handle: 'pod-pro', defaultVariant: 1, showStock: false } },
	'pod-mattress': { query: { handle: 'pod-pro', defaultVariant: 1, showStock: false } },
	'pod-cover-mattress': { query: { handle: 'pod-pro', defaultVariant: 1, showStock: false } },
	'gravity-blanket': { query: { handle: BlanketProducts.BrandedGravity.handle, defaultVariant: 0, showStock: false } },
	'the-pod-sheet-set': { query: { handle: SheetProducts.SheetSet.handle, defaultVariant: 0, showStock: false } },
	'melatonin-pro-bundle': {
		query: { handle: BundleProducts.MelatoninProBundle.handle, defaultVariant: 0, showStock: false },
	},
	'the-sleep-essentials-bundle': {
		query: { handle: BundleProducts.SleepEssentialBundle.handle, defaultVariant: 0, showStock: false },
	},
	eba234937b8d54e2d519b318707ea9713e6087b402b40670319c4c1e166e9208: {
		query: {
			handle: 'pod-upgrade',
			defaultVariant: 1,
			showStock: false,
		},
	},
	eba234937b8d54e2d519b318707ea9713e6087b402b40670319c4c1e166e92082: {
		query: {
			handle: 'pod-upgrade',
			defaultVariant: 1,
			showStock: false,
		},
	},
	dce234937b8d54e2r417b318707ea9713e6087b402b40670319c4c1e166e9101: {
		query: {
			handle: 'pod-upgrade',
			defaultVariant: 1,
			showStock: false,
		},
	},
	'air-lite-comforter': {
		query: { handle: ComforterProducts.AirLiteComforter.handle, defaultVariant: 1, showStock: false },
	},
	'lavender-sleep-spray': {
		query: { handle: SprayProducts.LavenderSpray.handle, defaultVariant: 0, showStock: false },
	},
	'hypervolt-plus': { query: { handle: HypericeProducts.HypervoltPlus.handle, defaultVariant: 0, showStock: false } },
	'hypervolt-2-pro': { query: { handle: HypericeProducts.HypervoltPlus.handle, defaultVariant: 0, showStock: false } },
	'venom-back': { query: { handle: HypericeProducts.VenomBack.handle, defaultVariant: 0, showStock: false } },
	'leg-pulse': { query: { handle: NormaTecProducts.LegPulse2.handle, defaultVariant: 0, showStock: false } },
	'pod-cover': {
		query: { handle: PodCoverProducts.Pod3CoverPerfect.handle, defaultVariant: 1, showStock: false },
	},
	'shop-pod-cover': {
		query: { handle: PodCoverProducts.Pod3CoverPerfect.handle, defaultVariant: 1, showStock: false },
	},
	'pod-pro-cover': {
		query: { handle: PodCoverProducts.Pod3CoverPerfect.handle, defaultVariant: 1, showStock: false },
	},
	'premium-mattress': {
		query: { handle: MattressProducts.FiveLayerDiscounted.handle, defaultVariant: 1, showStock: false },
	},
}

export const getStaticPaths: GetStaticPaths = async () => {
	const paths = Object.keys(productPages)
		.flatMap((p) => config.allRegions.map((l) => ({ params: { index: p, locale: l } })))
		.flatMap((it) => generatePathPermutationsForExperiments(`/product/${it.params.index}`).map((p) => ({ params: { ...it.params, experiments: p } })))

	if (process.env.NEXT_PUBLIC_IS_PROD) {
		return {
			paths,
			fallback: false,
		}
	}

	// Don't prerender products by default, only on production
	return {
		paths: [],
		fallback: 'blocking',
	}
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
	const pathname = ((params?.index || '') as string).trim()
	return {
		props: getProductProps(`/product/${pathname}`),
	}
}

export default function ProductPage(props: Props) {
	return <ProductCorePage {...props} />
}
